import { useState } from 'react'

import { useTranslation } from 'react-i18next'
import {
  currentPageParams,
  getDataClass,
  provideComponent,
  useDataScope,
} from 'scrivito'

import { DXPOverviewWidget } from './DXPOverviewWidgetClass'
import { OverviewHeaderActions } from './OverviewHeaderActions'
import { getImageAlt } from './utils/imageAlt'
import { getKanbanViewPresent } from './utils/kanbanViewPresent'
import { getTableCols } from './utils/tableCols'
import { DataErrorEditorNote } from '@/Components/DataErrorEditorNote'
import { DisplayCountIndicator } from '@/Components/DisplayCountIndicator'
import { EditorNote } from '@/Components/EditorNote'
import { EntityListLoadingIndicator } from '@/Components/EntityListLoadingIndicator'
import { EntityListNoResultsIndicator } from '@/Components/EntityListNoResultsIndicator'
import { EntitySearchHeader } from '@/Components/EntitySearchHeader/EntitySearchHeader'
import { useIsCardView } from '@/Components/EntityTable/CardView/useIsCardView'
import { EntityTable } from '@/Components/EntityTable/EntityTable'
import { DATA_SCOPE_ERROR, ErrorVariant } from '@/Components/error/constants'
import { ErrorPage } from '@/Components/error/Errors'
import { ErrorView } from '@/Components/error/ErrorView'
import { KanbanView } from '@/Components/KanbanView/KanbanView'
import { Loading } from '@/Components/Loading'
import { DataClassName } from '@/Data/const'
import { SchemaAttributes } from '@/Data/types'
import { useFeatureFlags } from '@/hooks/useFeatureFlagHandler'
import { useKanbanView } from '@/hooks/useKanbanView'
import { getSchemaName } from '@/utils/dataClassMap'
import { ensureString } from '@/utils/ensureString'
import { paramsToFilters } from '@/utils/filterParams'
import { getDataItems } from '@/utils/getDataItems'
import { getLabelsFromSchema } from '@/utils/getLabelsFromSchema'
import { getSchema } from '@/utils/getSchema'
import { getTableColumns } from '@/utils/getTableColumns'
import { getLocalizedSchemaAttributes } from '@/utils/schemaLocalization'

/** This is the overview of all items of an entity as a list or kanban view for example */
provideComponent(
  DXPOverviewWidget,
  () => {
    const dataScope = useDataScope()
    const { isEnabled } = useFeatureFlags()
    const { t } = useTranslation()
    const [isImporting, setIsImporting] = useState(false)

    const params = currentPageParams()
    const search = ensureString(params['search'] ?? null)
    const filter = ensureString(params['filter'] ?? null)
    const query = ensureString(params['query'] ?? null)
    const labels = ensureString(params['labels'] ?? null)
    const attributeFilters = paramsToFilters(params)
    // A search/filter/query/labels/attributeFilter is applied. Used both to
    // gate the empty/error indicators and to distinguish a user-recoverable
    // filter error (show the header + a clear-filters message) from a genuine
    // data/config error (the editor-only note).
    const hasActiveFilter =
      !!search ||
      !!filter ||
      !!query ||
      !!labels ||
      Object.keys(attributeFilters).length > 0
    // Remount the loading/no-results indicators whenever the query changes, so
    // Scrivito re-fires their { loading } fallback (it only shows on a fresh
    // mount). Keyed on the full params — stable across load-more (limit is
    // provider state, not a URL param), so the table itself never flashes.
    const queryKey = JSON.stringify(params)

    const dataClassName = dataScope?.dataClassName() || ''
    const dataClass = getDataClass(dataClassName)

    const { dataItems: fetchedDataItems, error: dataError } =
      getDataItems(dataScope)
    // PISA's `/list` endpoint 422s on a server-side `entityType` filter (see
    // EntityListContext), so the "Object" quick filter is applied here
    // instead, against the attribute each item already carries.
    const entityTypeFilter =
      dataClassName === DataClassName.LIST
        ? attributeFilters.entityType
        : undefined
    const dataItems = entityTypeFilter
      ? fetchedDataItems.filter(
          (item) => item.get('entityType') === entityTypeFilter,
        )
      : fetchedDataItems
    const { schema, error: schemaError } = getSchema(
      getSchemaName(dataClassName),
    )

    const schemaAttributes = getLocalizedSchemaAttributes(
      schema?.get('attributes') as SchemaAttributes,
    )

    const availableLabels = getLabelsFromSchema(schemaAttributes)

    const tableColumns = getTableColumns(
      getTableCols(dataClassName, isEnabled),
      schemaAttributes,
      availableLabels,
      ensureString(labels),
    )

    const imageAlt = getImageAlt(dataClassName)

    const kanbanViewPresent = getKanbanViewPresent(dataClassName)
    const { active: kanbanViewActive, toggle } =
      useKanbanView(kanbanViewPresent)

    // In the lists card view the count indicator lives inside the card's
    // select-all header (matching the design), so suppress the standalone one.
    const showCardView = useIsCardView(dataClassName)

    if (dataError === DATA_SCOPE_ERROR) {
      return <EditorNote>No Data Scope is defined</EditorNote>
    }

    if (!dataClass) {
      return <EditorNote>No Data Class is defined</EditorNote>
    }

    // A genuine data/config error (no active filter) stays the editor-only
    // note. A filter that the backend rejects falls through to the normal
    // render below, which shows the header + a recoverable message so the
    // end user can clear the filter instead of facing a blank page.
    if (dataError && !hasActiveFilter) {
      return <DataErrorEditorNote error={dataError} />
    }

    if (!schema || schemaError) {
      return <ErrorPage variant={ErrorVariant.InternalServerError} />
    }

    const STATUS_FILTER_ENTITIES: DataClassName[] = [
      DataClassName.LIST,
      DataClassName.OPPORTUNITY,
      DataClassName.QUOTE,
      DataClassName.ORDER,
      DataClassName.TICKET,
    ]
    const ENTITIES_WITHOUT_TYPE_FILTER: DataClassName[] = [
      DataClassName.LIST,
      DataClassName.OPPORTUNITY,
      DataClassName.QUOTE,
      DataClassName.ORDER,
      DataClassName.LEAD,
      DataClassName.TICKET,
    ]
    const ENTITIES_WITHOUT_GROUP_FILTER: DataClassName[] = [
      DataClassName.OPPORTUNITY,
      DataClassName.QUOTE,
      DataClassName.ORDER,
      DataClassName.LEAD,
      DataClassName.TICKET,
    ]
    const DATE_RANGE_FILTER_ENTITIES: DataClassName[] = [DataClassName.ACTIVITY]

    const matches = (list: DataClassName[]) =>
      list.includes(dataClassName as DataClassName)

    const showStatusFilter = matches(STATUS_FILTER_ENTITIES)
    const showTypeFilter = !matches(ENTITIES_WITHOUT_TYPE_FILTER)
    const showGroupFilter = !matches(ENTITIES_WITHOUT_GROUP_FILTER)
    const showDateRangeFilter = matches(DATE_RANGE_FILTER_ENTITIES)
    const showObjectFilter = dataClassName === DataClassName.LIST

    return (
      <>
        <EntitySearchHeader
          dataClassName={dataClassName}
          showStatusFilter={showStatusFilter}
          showTypeFilter={showTypeFilter}
          showGroupFilter={showGroupFilter}
          showDateRangeFilter={showDateRangeFilter}
          showObjectFilter={showObjectFilter}
        >
          <OverviewHeaderActions
            dataClassName={dataClassName}
            dataClass={dataClass}
            kanbanViewPresent={kanbanViewPresent}
            kanbanViewActive={kanbanViewActive}
            onToggleKanban={toggle}
            onImportingChange={setIsImporting}
          />
        </EntitySearchHeader>

        <div className="row">
          <div className="col-sm-12">
            {dataError ? (
              <ErrorView
                headline={t('search.filterError.title')}
                text={t('search.filterError.subtitle')}
                image={<div className="spaceman-sad" />}
              />
            ) : (
              <>
                {!showCardView && <DisplayCountIndicator />}
                {hasActiveFilter && (
                  <>
                    <EntityListLoadingIndicator key={`loading-${queryKey}`} />
                    <EntityListNoResultsIndicator
                      key={`no-results-${queryKey}`}
                    />
                  </>
                )}
                {!isImporting && (!kanbanViewPresent || !kanbanViewActive) && (
                  <EntityTable
                    tableColumns={tableColumns}
                    dataItems={dataItems}
                    schema={schema}
                    dataClassName={dataClassName}
                    imageAlt={imageAlt}
                  />
                )}
                {!isImporting && kanbanViewPresent && kanbanViewActive && (
                  <KanbanView
                    dataItems={dataItems}
                    schema={schema}
                    dataClassName={dataClassName}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </>
    )
  },
  { loading: Loading },
)
